import { Alert, Anchor, Badge, Group, Stack, Text } from "@mantine/core";
import { IconBuilding, IconPercentage, IconUsers } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { EmailVerificationCard } from "../../components/EmailVerificationCard";
import { PageHeader, SectionBlock, StatCard } from "../../components/ui";
import { isCompanyAdmin, ORG_KYC_COPY, orgIsVerified } from "../../lib/companyKyc";
import { companyApi, consultancyApi } from "../../lib/api";
import { NAV_ICONS } from "../../lib/navCopy";

export function ConsultancyDashboardPage() {
  const companyQuery = useQuery({ queryKey: ["my-company"], queryFn: companyApi.myCompany });
  const recruitersQuery = useQuery({
    queryKey: ["consultancy-recruiters"],
    queryFn: consultancyApi.listRecruiters,
  });
  const rateCardQuery = useQuery({
    queryKey: ["consultancy-rate-card"],
    queryFn: consultancyApi.rateCard,
  });

  const recruiters = recruitersQuery.data ?? [];
  const rateCard = rateCardQuery.data;

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.dashboard}
        title={companyQuery.data?.company_name ?? "Consultancy"}
        subtitle="Manage your recruiters and see the split AxioHire applies to your placements."
      />

      {companyQuery.data && !orgIsVerified(companyQuery.data.verification_status) && (
        <Alert color="yellow" title={ORG_KYC_COPY.unverifiedBannerTitle}>
          {isCompanyAdmin(companyQuery.data) ? (
            <>
              {ORG_KYC_COPY.unverifiedConsultancyBody}{" "}
              <Anchor component={Link} to="/consultancy/kyc" fw="var(--axio-font-weight-semibold)">
                {ORG_KYC_COPY.pageTitleConsultancy}
              </Anchor>
            </>
          ) : (
            ORG_KYC_COPY.restrictedMemberConsultancyBody
          )}
        </Alert>
      )}

      <EmailVerificationCard />

      <Group grow>
        <StatCard label="Recruiters" value={String(recruiters.length)} icon={IconUsers} />
        <StatCard
          label="Your cut"
          value={rateCard ? `${rateCard.recruiter_fee_pct}%` : "—"}
          icon={IconPercentage}
        />
        <StatCard
          label="Platform"
          value={rateCard ? `${rateCard.platform_fee_pct}%` : "—"}
          icon={IconBuilding}
        />
      </Group>

      <SectionBlock
        title="Next steps"
        description="Invite recruiters so they can work open marketplace jobs under your rate card."
      >
        <Group gap="sm">
          <Text component={Link} to="/consultancy/recruiters" size="sm" className="quickLinkChip">
            Manage recruiters
          </Text>
          <Text component={Link} to="/consultancy/jobs" size="sm" className="quickLinkChip">
            Browse jobs
          </Text>
          <Text component={Link} to="/consultancy/candidates" size="sm" className="quickLinkChip">
            Candidate pool
          </Text>
          <Text component={Link} to="/consultancy/payments" size="sm" className="quickLinkChip">
            Payments
          </Text>
          <Text component={Link} to="/consultancy/rate-card" size="sm" className="quickLinkChip">
            View rate card
          </Text>
        </Group>
        {rateCard && (
          <Badge mt="sm" variant="outline" color="violet">
            {rateCard.name}: {rateCard.recruiter_fee_pct}/{rateCard.sourcing_fee_pct}/
            {rateCard.platform_fee_pct}
          </Badge>
        )}
      </SectionBlock>
    </Stack>
  );
}
